import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';
import { Subject, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { DynamicFormComponent } from 'src/app/shared/components/dynamic-form/dynamic-form/dynamic-form.component';
import { MatStrokedButtonBorderDirective } from 'src/app/shared/directives';
import { DynamicFieldsForPayment, DynamicsFieldsService } from 'src/app/shared/services';
import { InformationCollectorByPaymentService } from './services/information-collector-by-payment.service';

@Component({
    selector: 'app-information-collector-by-payment',
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatStepperModule,
        DynamicFormComponent,
        MatStrokedButtonBorderDirective,
        MatDividerModule,
    ],
    templateUrl: './information-collector-by-payment.component.html',
    styleUrl: './information-collector-by-payment.component.scss',
})
export class InformationCollectorByPaymentComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();
    public dynamicFieldsForPayment: DynamicFieldsForPayment[] = [];
    public formGroups: FormGroup[] = [];
    public isCustomerReferenceRequired = true;

    @ViewChildren('dynamicForms') dynamicForms: QueryList<DynamicFormComponent>;
    @Input() stepperOn = true;
    @Output() processCompleted = new EventEmitter<boolean>();
    @Input() withButtons = true;

    constructor(
        private dynamicsFieldsService: DynamicsFieldsService,
        private informationCollectorByPaymentService: InformationCollectorByPaymentService // private configService: ConfigService
    ) {}

    ngOnInit(): void {
        this.isCustomerReferenceRequired = this.dynamicsFieldsService.isCustomerReferenceRequired();
        this.getDynamicFieldsForPayment();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    getDynamicFieldsForPayment(): void {
        this.dynamicsFieldsService
            .getLoadingDynamicFields()
            .pipe(
                filter((loading) => !loading),
                switchMap(() => {
                    return this.dynamicsFieldsService.getDynamicFieldsForPayment$().pipe(
                        map((dynamicFieldsForPayment) => {
                            return this.removeSummaryStep(dynamicFieldsForPayment);
                        })
                    );
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (dynamicFieldsForPayment) => {
                    this.dynamicFieldsForPayment = dynamicFieldsForPayment;
                    setTimeout(() => {
                        this.formGroups = this.dynamicForms?.map((dynamicForm) => dynamicForm?.dynamicFormGroup) || [];
                        this.informationCollectorByPaymentService.setFormGroups(this.formGroups);
                        this.saveCollectorFormsOnChange();
                        this.subscribeToCustomerGetInitialData();
                    }, 0);
                },
            });
    }

    removeSummaryStep(dynamicFieldsForPayment: DynamicFieldsForPayment[]): DynamicFieldsForPayment[] {
        return dynamicFieldsForPayment.filter((section) => !section?.label?.toLowerCase()?.includes('summary'));
    }

    onSubmitCollector(): void {
        if (this.informationCollectorByPaymentService.instant_formGroupsValidateForms) {
            this.processCompleted.emit(true);
        } else {
            this.markFormAsTouched();
        }
    }

    onCancelProccess(): void {
        this.processCompleted.emit(false);
    }

    saveCollectorFormsOnChange(): void {
        this.formGroups.forEach((form, index) => {
            let prevValue: (FormGroup | null)[] = [];

            form.statusChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
                if (form.valid) {
                    this.informationCollectorByPaymentService.setCollectorForms(form, index!);
                    prevValue[index] = form;
                    this.informationCollectorByPaymentService.setFormGroups(this.formGroups);
                } else if (prevValue[index] != null) {
                    this.informationCollectorByPaymentService.setCollectorForms(null, index!);
                    this.informationCollectorByPaymentService.setFormGroups([]);
                    prevValue[index] = null;
                }
            });
        });
    }

    subscribeCollectorForms(): void {
        this.informationCollectorByPaymentService
            .getCollectorForms$()
            .pipe(take(1))
            .subscribe({
                next: (forms) => {
                    if (forms?.length) {
                        forms.forEach((form, index) => {
                            if (form) {
                                this.formGroups[index].patchValue(form.getRawValue());
                            }
                        });
                    }
                },
            });
    }

    subscribeToCustomerGetInitialData(): void {
        this.subscribeCollectorForms();
    }

    onContinue(form: FormGroup): void {
        form.markAllAsTouched();
    }

    markFormAsTouched(): void {
        this.dynamicForms.forEach((form) => {
            form?.dynamicFormGroup?.markAllAsTouched();
        });
    }
}
